import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import EditIcon from '@mui/icons-material/Edit';

import { getFragmentData, graphql } from '@e-socna/data-layer';
import type { FragmentType } from '@e-socna/data-layer';

import { NoData } from '../../components/Illustrations/NoData';
import { MembershipCard } from '../../components/MembershipCard';
import { RightDrawer } from '../../components/RightDrawer';
import { PersonForm } from '../../forms/ThirdParty/Person';
import { SpeedDial } from '../../components/SpeedDial';
import { ThirdPartyFormTab } from '../../forms/ThirdParty/enums/tab.enum';
import { FormVariant } from '../../forms';
import { ResponsibilityHandoverAction } from '../../forms/ResponsibilityHandover';

export const PersonPage_Memberships_Person_Fragment = graphql(`
  fragment PersonPage_Memberships_Person_Fragment on Person {
    _id
    isInternal
    activeMemberships {
      organizationId
      ...PersonMembershipCardCmp_PersonMembership_Fragment
    }
    ...ThirdPartyForm_Person_Fragment
  }
`);

interface Props {
  readonly paths: Readonly<{
    consult: string;
    edit: string;
  }>;
  readonly member: FragmentType<typeof PersonPage_Memberships_Person_Fragment>;
}

export const Memberships = ({ paths, member: pMember }: Readonly<Props>) => {
  const member = getFragmentData(
    PersonPage_Memberships_Person_Fragment,
    pMember,
  );

  const { pathname: currentPath } = useLocation();
  const navigate = useNavigate();

  const isInEditionMode = currentPath === paths.edit;
  const leaveEditionMode = useCallback(
    () => void navigate(paths.consult),
    [navigate, paths.consult],
  );

  const { activeMemberships } = member;

  return (
    <>
      <Box sx={{ pt: 4, width: '100%' }}>
        {!activeMemberships.length ? (
          <NoData />
        ) : (
          <Grid container spacing={2}>
            {activeMemberships.map((activeMembership) => (
              <MembershipCard
                key={activeMembership.organizationId}
                personMembership={activeMembership}
                personMembershipAction={
                  member.isInternal ? (
                    <ResponsibilityHandoverAction
                      organizationId={activeMembership.organizationId}
                      personId={member._id}
                    />
                  ) : null
                }
              />
            ))}
          </Grid>
        )}
      </Box>
      <SpeedDial
        actions={[
          {
            id: 'editPersonMemberships',
            icon: <EditIcon />,
            name: 'Modifier Organisations',
            to: paths.edit,
          },
        ]}
        title="Actions possibles sur les Organisations de la Personne"
      />
      <RightDrawer isOpen={isInEditionMode} onDrawerClose={leaveEditionMode}>
        <PersonForm
          formVariant={FormVariant.Modification}
          initialValues={member}
          isInternal={member.isInternal}
          onCancel={leaveEditionMode}
          onSuccess={leaveEditionMode}
          personId={member._id}
          stepIdsSubset={[
            ThirdPartyFormTab.PersonMemberships,
            ThirdPartyFormTab.PersonMembershipsDetails,
          ]}
          title="Formulaire de modification des Organisations de la Personne"
        />
      </RightDrawer>
    </>
  );
};
